/**
 * React Query hooks of the Flotte pages: the fleet list and the eager
 * fetch of the five vehicle endpoints (FIC_VEH, FIC_POL_VEH,
 * TAB_GARANTIES_VEH, TAB_SIN_VEH, TAB_DOCS_VEH — run in parallel on mount so
 * the « Sinistres » tab count is known before the tab is opened).
 *
 * Keys are scoped by VEOS environment and session (a session switch reloads
 * anyway). Errors surface as the ERP message (`Error.message`); a missing
 * environment surfaces as « Aucun environnement VEOS actif. ».
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  buildFleetListParams,
  fleetService,
  type FleetListFilters,
  type FleetVehicle,
  type VehicleClaim,
  type VehicleContract,
  type VehicleDetails,
} from '../../api/fleetService';
import { useExtranetAuthStore } from '../../auth/store';
import type { DocumentItem } from '../shared/documentItems';
import type { GuaranteeTableRow } from '../shared/guaranteesTableConfig';

/** The loading / error / data triple a card consumes. */
export interface FleetQueryState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

function toState<T>(query: UseQueryResult<T>, fallback: T): FleetQueryState<T> {
  return {
    data: query.data ?? fallback,
    loading: query.isLoading,
    error: query.error ? query.error.message : null,
  };
}

/** Root of every Flotte query key. */
export function fleetQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
  ...parts: string[]
): readonly unknown[] {
  return ['extranet', 'fleet', registrySlug, sessionId, ...parts];
}

function useErpScope() {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  return { registrySlug, sessionId };
}

const NO_VEHICLES: FleetVehicle[] = [];

/**
 * `WS2_EA_TAB_GEN_VEH` — the fleet matching the list toolbar's search
 * (`query`, fiche 22987). The key carries the effective request params
 * (blank search ⇒ the unfiltered key); the previous rows stay on screen
 * while a new search loads.
 */
export function useFleetVehicles(filters: FleetListFilters = {}): FleetQueryState<FleetVehicle[]> {
  const { registrySlug, sessionId } = useErpScope();
  const params = buildFleetListParams(filters);
  const query = useQuery<FleetVehicle[]>({
    queryKey: [...fleetQueryKey(registrySlug, sessionId, 'vehicles'), params],
    queryFn: () => fleetService.fetchVehicles(registrySlug, filters),
    retry: false,
    placeholderData: keepPreviousData,
  });
  return toState(query, NO_VEHICLES);
}

/** The five datasets of a vehicle detail. */
export interface VehicleDetailData {
  details: FleetQueryState<VehicleDetails | null>;
  contract: FleetQueryState<VehicleContract | null>;
  guarantees: FleetQueryState<GuaranteeTableRow[]>;
  claims: FleetQueryState<VehicleClaim[]>;
  documents: FleetQueryState<DocumentItem[]>;
}

const NO_GUARANTEES: GuaranteeTableRow[] = [];
const NO_CLAIMS: VehicleClaim[] = [];
const NO_DOCUMENTS: DocumentItem[] = [];

/** Eager, parallel fetch of the vehicle `idNpol` / `idVeh`. */
export function useVehicleDetailData(idNpol: string, idVeh: string): VehicleDetailData {
  const { registrySlug, sessionId } = useErpScope();
  const enabled = idNpol !== '' && idVeh !== '';
  const key = (part: string) => fleetQueryKey(registrySlug, sessionId, 'vehicle', idNpol, idVeh, part);

  const details = useQuery<VehicleDetails | null>({
    queryKey: key('details'),
    queryFn: () => fleetService.fetchVehicle(registrySlug, idNpol, idVeh),
    enabled,
    retry: false,
  });
  const contract = useQuery<VehicleContract | null>({
    queryKey: key('contract'),
    queryFn: () => fleetService.fetchVehicleContract(registrySlug, idNpol, idVeh),
    enabled,
    retry: false,
  });
  const guarantees = useQuery<GuaranteeTableRow[]>({
    queryKey: key('guarantees'),
    queryFn: () => fleetService.fetchVehicleGuarantees(registrySlug, idNpol, idVeh),
    enabled,
    retry: false,
  });
  const claims = useQuery<VehicleClaim[]>({
    queryKey: key('claims'),
    queryFn: () => fleetService.fetchVehicleClaims(registrySlug, idNpol, idVeh),
    enabled,
    retry: false,
  });
  const documents = useQuery<DocumentItem[]>({
    queryKey: key('documents'),
    queryFn: () => fleetService.fetchVehicleDocuments(registrySlug, idNpol, idVeh),
    enabled,
    retry: false,
  });

  return {
    details: toState(details, null),
    contract: toState(contract, null),
    guarantees: toState(guarantees, NO_GUARANTEES),
    claims: toState(claims, NO_CLAIMS),
    documents: toState(documents, NO_DOCUMENTS),
  };
}
